import { CsvExport, ExcelExport, ExcelSheet } from '../models/api/core/export/export';
import {
  buildColumnTypes,
  coerceExportNumericValue,
  ExportColumnDefinition,
  hasTypedColumns,
  resolveColumnType,
} from './export-column.util';

export class ClaimExportMapper {
  static toCsvExport(rows: any[], columns: any[], fileName: string): CsvExport {
    const headers = columns.map((col) => ClaimExportMapper.getHeaderText(col));
    const dataRows = rows.map((row) =>
      columns.map((col) => ClaimExportMapper.getColumnValue(row, col)),
    );

    return {
      fileName,
      headers,
      rows: dataRows,
    };
  }

  static toExcelExport(
    rows: any[],
    columns: any[],
    fileName: string,
    sheetLabel: string,
  ): ExcelExport {
    const headers = columns.map((col) => ClaimExportMapper.getHeaderText(col));
    const dataRows = rows.map((row) =>
      columns.map((col) => ClaimExportMapper.getColumnValue(row, col)),
    );
    const columnTypes = buildColumnTypes(columns);

    const sheet: ExcelSheet = {
      label: sheetLabel,
      headers,
      rows: dataRows,
      columnTypes: hasTypedColumns(columnTypes) ? columnTypes : undefined,
    };

    return {
      fileName,
      sheets: [sheet],
    };
  }

  private static getHeaderText(column: any): string {
    const headerMappings: Record<string, string> = {
      ClaimNumber: 'Claim',
      LossDate: 'Date of Loss',
      DateLastWorked: 'Last Day Worked',
    };

    return (
      headerMappings[column.propertyName] ||
      column.headerText ||
      column.propertyName
    );
  }

  private static getColumnValue(row: any, column: ExportColumnDefinition): any {
    if (!row || !column) return undefined;
    const propertyName = column.propertyName;
    const keys = Object.keys(row);
    const lowerKeyMap = keys.reduce(
      (acc, key) => {
        acc[key.toLowerCase()] = key;
        return acc;
      },
      {} as Record<string, string>,
    );

    if (
      propertyName.toLowerCase() === 'claimnumber' ||
      propertyName.toLowerCase() === 'filenumber'
    ) {
      const fileKey = lowerKeyMap['filenumber'];
      return fileKey ? row[fileKey] : undefined;
    }

    const actualKey = lowerKeyMap[propertyName.toLowerCase()];
    const value = actualKey ? row[actualKey] : undefined;
    const columnType = resolveColumnType(column);

    if (
      (columnType === 'currency' || columnType === 'number') &&
      value != null
    ) {
      const num = coerceExportNumericValue(value);
      if (num != null) {
        return num;
      }
      return value;
    }

    if (ClaimExportMapper.isDateFormat(column.format) && value) {
      if (value === '0001-01-01T00:00:00') {
        return '';
      }
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
      }
      return '';
    }

    if (column.stripHtml && typeof value === 'string') {
      return ClaimExportMapper.htmlToPlainText(value);
    }

    return value;
  }

  /** True when the column format string is a .NET date format (e.g. MM/dd/yyyy, yyyy-MM-dd). */
  private static isDateFormat(format: string | undefined): boolean {
    if (!format) return false;
    return /[yY]{2,4}/.test(format) && /M{1,4}/.test(format) && /[dD]{1,2}/.test(format);
  }

  /** Plain text for spreadsheet exports (matches on-screen meaning, no markup). */
  private static htmlToPlainText(html: string): string {
    if (!html?.trim()) {
      return '';
    }
    const normalized = html
      .replace(/\r\n/g, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, '\n')
      .replace(/<\/table>/gi, '\n');
    const doc = new DOMParser().parseFromString(normalized, 'text/html');
    return (doc.body.textContent ?? '')
      .replace(/\u00a0/g, ' ')
      .replace(/\n[ \t]+/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }
}
